import { Store } from './store';
import type {
  CommandAck,
  CommandType,
  ConnectionState,
  LobbySession,
  LobbyDirectoryPage,
  LobbySnapshot,
} from './types';

interface StoredSession {
  code: string;
  playerId: string;
  sessionToken: string;
  wsUrl: string;
  seq: number;
  version: number;
}

interface PendingCommand {
  resolve: (ack: CommandAck) => void;
  reject: (error: Error) => void;
  timeout: number;
}

export interface ChatMessage {
  id: number;
  player_name: string;
  player_avatar: string;
  player_swirl: string;
  text: string;
}

const SESSION_KEY = 'wa_v2_session';

export class RealtimeClient {
  readonly store = new Store();
  private readonly apiBase: string;
  private enabled = false;
  private socket: WebSocket | null = null;
  private session: StoredSession | null = null;
  private pending = new Map<string, PendingCommand>();
  private heartbeatTimer: number | null = null;
  private degradedTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempt = 0;
  private closedIntentionally = false;
  private commandChain: Promise<unknown> = Promise.resolve();
  private lastResumeAt = 0;

  constructor(apiBase: string) {
    this.apiBase = `${apiBase}/v2`;
    this.session = this.readSession();
    this.watchLifecycle();
  }

  /**
   * Browser drosseln Zeitgeber in Hintergrund-Tabs auf etwa einen Takt pro
   * Minute, Mobilgeraete frieren Seiten vollstaendig ein. Die Verbindung
   * stirbt dadurch unbemerkt. Sobald die Seite wieder vorne ist, wird der
   * Zustand nachgeholt und bei Bedarf sofort neu verbunden, statt auf den
   * naechsten Versuch mit Wartezeit zu warten.
   */
  private watchLifecycle(): void {
    if (typeof document === 'undefined') return;
    const aufwachen = () => this.resumeNow();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') aufwachen();
    });
    window.addEventListener('pageshow', aufwachen);
    window.addEventListener('focus', aufwachen);
    window.addEventListener('online', aufwachen);
  }

  /**
   * Holt den aktuellen Zustand und stellt die Verbindung wieder her.
   * Mehrfachaufrufe innerhalb von zwei Sekunden werden zusammengefasst,
   * weil mehrere Ereignisse beim Zuruueckkehren fast gleichzeitig feuern.
   */
  private resumeNow(): void {
    if (!this.enabled || !this.session) return;
    const jetzt = Date.now();
    if (jetzt - this.lastResumeAt < 2000) return;
    this.lastResumeAt = jetzt;

    void this.fetchSnapshot();

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify({ type: 'ping', at: jetzt }));
      } catch {
        this.reconnectAttempt = 0;
        this.connect();
      }
      return;
    }
    if (this.socket && this.socket.readyState === WebSocket.CONNECTING) return;

    // Wartezeit aus dem Rueckzugsverfahren ueberspringen.
    this.reconnectAttempt = 0;
    this.connect();
  }

  async initialize(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBase}/config`, { cache: 'no-store' });
      const config = await response.json();
      this.enabled = response.ok && config.enabled === true;
    } catch {
      this.enabled = false;
    }
    this.setConnection(this.enabled ? 'offline' : 'disabled');
    return this.enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isActive(code?: string): boolean {
    return this.enabled && !!this.session && (!code || this.session.code === code);
  }

  hasSession(): boolean {
    return !!this.session;
  }

  async create(payload: Record<string, unknown>): Promise<LobbySession> {
    await this.ensureEnabled();
    const response = await fetch(`${this.apiBase}/lobbies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await this.parseSession(response);
    this.activate(data);
    return data;
  }

  async join(code: string, payload: Record<string, unknown>): Promise<LobbySession> {
    await this.ensureEnabled();
    const response = await fetch(`${this.apiBase}/lobbies/${encodeURIComponent(code)}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await this.parseSession(response);
    this.activate(data);
    return data;
  }

  async listLobbies(offset = 0, limit = 50): Promise<LobbyDirectoryPage> {
    await this.ensureEnabled();
    const response = await fetch(`${this.apiBase}/lobbies?offset=${offset}&limit=${limit}`, {
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Lobby directory is unavailable');
    return await response.json() as LobbyDirectoryPage;
  }

  async resume(): Promise<LobbySession | null> {
    await this.ensureEnabled();
    if (!this.session) return null;
    const response = await fetch(`${this.apiBase}/lobbies/${this.session.code}/resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_token: this.session.sessionToken,
        last_seq: this.session.seq,
      }),
    });
    if (!response.ok) {
      // Nur bei einer endgueltigen Abweisung verwerfen. Vorher loeschte jeder
      // Fehlschlag die Sitzung -- ein 500 oder ein Netzaussetzer beim Start
      // kostete damit Platz und Punktestand, obwohl der Token gueltig war.
      // 409 bleibt erhalten: der Name kann spaeter wieder frei werden.
      if ([401, 403, 404, 410].includes(response.status)) this.clear();
      return null;
    }
    const data = await response.json() as LobbySession;
    this.activate(data);
    return data;
  }

  command(type: CommandType, payload: Record<string, unknown> = {}): Promise<CommandAck> {
    const run = () => this.dispatchCommandWithRetry(type, payload);
    const result = this.commandChain.then(run, run);
    this.commandChain = result.then(() => undefined, () => undefined);
    return result;
  }

  private async dispatchCommandWithRetry(type: CommandType, payload: Record<string, unknown>): Promise<CommandAck> {
    let ack = await this.dispatchCommand(type, payload);
    if (!ack.ok && ack.error_code === 'VERSION_CONFLICT') {
      await this.fetchSnapshot();
      ack = await this.dispatchCommand(type, payload);
    }
    return ack;
  }

  private async dispatchCommand(type: CommandType, payload: Record<string, unknown>): Promise<CommandAck> {
    if (!this.session) throw new Error('No active realtime session');
    const command = {
      command_id: crypto.randomUUID(),
      type,
      expected_version: this.session.version,
      payload,
    };
    if (this.socket?.readyState === WebSocket.OPEN) {
      return new Promise<CommandAck>((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          this.pending.delete(command.command_id);
          reject(new Error('Command timed out'));
        }, 8000);
        this.pending.set(command.command_id, { resolve, reject, timeout });
        this.socket?.send(JSON.stringify({ type: 'command', command }));
      });
    }
    const response = await fetch(`${this.apiBase}/lobbies/${this.session.code}/commands`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.session.sessionToken}`,
      },
      body: JSON.stringify(command),
    });
    const ack = await response.json() as CommandAck;
    this.acceptAck(ack);
    await this.fetchSnapshot();
    return ack;
  }

  subscribeSnapshot(listener: (snapshot: LobbySnapshot) => void): () => void {
    return this.store.subscribe((state) => {
      if (state.snapshot) listener(state.snapshot);
    });
  }

  subscribeConnection(listener: (state: ConnectionState) => void): () => void {
    return this.store.subscribe((state) => listener(state.connection));
  }

  async fetchChat(sinceId: number): Promise<ChatMessage[]> {
    if (!this.session) return [];
    const response = await fetch(
      `${this.apiBase}/lobbies/${this.session.code}/chat?since_id=${sinceId}`,
      { headers: { Authorization: `Bearer ${this.session.sessionToken}` } },
    );
    return response.ok ? await response.json() as ChatMessage[] : [];
  }

  clear(): void {
    this.closedIntentionally = true;
    this.stopTimers();
    this.socket?.close();
    this.socket = null;
    this.session = null;
    localStorage.removeItem(SESSION_KEY);
    this.store.set({ snapshot: null, connection: this.enabled ? 'offline' : 'disabled' });
  }

  private async ensureEnabled(): Promise<void> {
    if (!this.enabled) {
      const ready = await this.initialize();
      if (!ready) throw new Error('Realtime v2 is unavailable');
    }
  }

  private async parseSession(response: Response): Promise<LobbySession> {
    const data = await response.json();
    if (!response.ok) {
      const detail = data.detail;
      throw new Error(detail?.message ?? detail ?? 'Lobby request failed');
    }
    return data as LobbySession;
  }

  private activate(data: LobbySession): void {
    this.session = {
      code: data.lobby.code,
      playerId: data.player_id,
      sessionToken: data.session_token,
      wsUrl: data.ws_url,
      seq: data.lobby.seq,
      version: data.lobby.version,
    };
    this.closedIntentionally = false;
    this.persistSession();
    this.acceptSnapshot(data.lobby);
    this.connect();
  }

  private connect(): void {
    if (!this.session || !this.enabled) return;
    this.stopTimers();
    this.socket?.close();
    this.setConnection('connecting');
    const socket = new WebSocket(this.session.wsUrl);
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (!this.session) return;
      this.reconnectAttempt = 0;
      this.setConnection('connected');
      socket.send(JSON.stringify({
        type: 'hello',
        session_token: this.session.sessionToken,
        last_seq: this.session.seq,
      }));
      this.heartbeatTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'ping', at: Date.now() }));
        }
      }, 10_000);
    });
    socket.addEventListener('message', (event) => this.onMessage(event));
    socket.addEventListener('close', () => this.onClose());
    socket.addEventListener('error', () => socket.close());
  }

  private onMessage(event: MessageEvent<string>): void {
    const message = JSON.parse(event.data);
    if (message.type === 'snapshot') {
      this.acceptSnapshot(message.snapshot as LobbySnapshot);
      return;
    }
    if (message.type === 'ack') {
      const ack = message as CommandAck & { type: string };
      this.acceptAck(ack);
    }
  }

  private acceptAck(ack: CommandAck): void {
    if (this.session) {
      this.session.version = Math.max(this.session.version, ack.version);
      this.session.seq = Math.max(this.session.seq, ack.seq);
      this.persistSession();
    }
    const pending = this.pending.get(ack.command_id);
    if (pending) {
      window.clearTimeout(pending.timeout);
      this.pending.delete(ack.command_id);
      pending.resolve(ack);
    }
    if (!ack.ok && ack.error_code === 'VERSION_CONFLICT') void this.fetchSnapshot();
  }

  private acceptSnapshot(snapshot: LobbySnapshot): void {
    if (this.session && snapshot.seq < this.session.seq) return;
    if (this.session) {
      this.session.seq = snapshot.seq;
      this.session.version = snapshot.version;
      this.persistSession();
    }
    this.store.set({ snapshot });
  }

  private onClose(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    if (this.closedIntentionally || !this.session) return;
    this.setConnection(navigator.onLine ? 'connecting' : 'offline');
    const delay = Math.min(10_000, 500 * 2 ** this.reconnectAttempt) + Math.random() * 250;
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    this.degradedTimer = window.setTimeout(() => {
      this.setConnection('degraded');
      void this.fetchSnapshot();
      this.degradedTimer = window.setInterval(() => void this.fetchSnapshot(), 10_000);
    }, 10_000);
  }

  private async fetchSnapshot(): Promise<void> {
    if (!this.session) return;
    try {
      const response = await fetch(`${this.apiBase}/lobbies/${this.session.code}/snapshot`, {
        headers: { Authorization: `Bearer ${this.session.sessionToken}` },
        cache: 'no-store',
      });
      if (response.ok) this.acceptSnapshot(await response.json() as LobbySnapshot);
    } catch {
      this.setConnection('offline');
    }
  }

  private setConnection(connection: ConnectionState): void {
    this.store.set({ connection });
  }

  private stopTimers(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    if (this.degradedTimer !== null) {
      window.clearTimeout(this.degradedTimer);
      window.clearInterval(this.degradedTimer);
    }
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.heartbeatTimer = null;
    this.degradedTimer = null;
    this.reconnectTimer = null;
  }

  private persistSession(): void {
    if (this.session) localStorage.setItem(SESSION_KEY, JSON.stringify(this.session));
  }

  private readSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) as StoredSession : null;
    } catch {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  }
}
